import type {
  Appointment,
  EpisodeOfCare,
  Patient,
  Practitioner,
  QuestionnaireResponse,
} from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import {
  ActionIcon,
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Group,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notify } from '@/lib/notify';
import { getUserFacingErrorMessage, UserFacingError } from '@/lib/userFacingError';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconEdit,
  IconShield,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAppointmentGroupRef, isCohortGroupAppointment } from '@/lib/groupTherapy';
import { requestGroupSharedNoteFanout, requestGroupSharedNoteMergeFromPatient } from '@/lib/groupSharedNoteFanoutService';
import { useMedplumSubscriptionRefresh } from '../../hooks/useMedplumSubscriptionRefresh';
import {
  buildAppointmentByIdSubscriptionCriteria,
  buildEncounterQuestionnaireResponseSubscriptionCriteria,
  buildPatientQuestionnaireResponseSubscriptionCriteria,
} from '../../lib/fhirRealtimeCriteria';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isCohortNoteContext, selectCohortAppointmentNote } from './utils/selectCohortAppointmentNote';
import {
  CohortProgressNoteTitleExtras,
  type CohortNoteKind,
} from './components/CohortProgressNoteTitleExtras';
import { Loading } from '../../components/Loading';
import { usePermissions } from '../../contexts/PermissionContext';
import type { Role } from '../../lib/rbacRoles';
import { useCurrentUserPractitioner } from '../../hooks/useCurrentUserPractitioner';
import { useSupervisedPractitionerIds } from '../../hooks/useSupervisedPractitionerIds';
import { resolveCurrentUserPractitioner } from '../../lib/practitionerIdentity';
import {
  EPISODE_OF_CARE_CONTEXT_EXTENSION,
  THERAPY_SERVICE_TYPE_SYSTEM,
} from '../../lib/caseloadFhirConstants';
import { useGeminiAiAccess } from '../../hooks/useGeminiAiOptOut';
import {
  chartScopeFromRoleAndPractitioner,
  canViewMedplumEpisodeOfCare,
} from '../../lib/rbac/eocChartScope';
import { getViewScope } from '../../lib/rbac/scopeAccess';
import {
  canAuthorClinicalNoteForRoles,
  canViewPsychotherapyNote,
  isSessionCareManager,
} from '../../lib/chart/chartDocumentAccess';
import { requestAiNoteRevision } from '../../lib/aiNoteRevisionService';
import { useDebouncedAiNoteCheck } from './hooks/useDebouncedAiNoteCheck';
import { requestSignedNoteExport } from '../../lib/signedNoteExportService';
import { readEncounterSessionTiming } from '../../lib/encounterSessionTiming';
import {
  isSessionTranscriptReady,
  requestAiNoteGeneration,
  requestChimeTranscriptStatusCheck,
  shouldPollTranscriptStatus,
  type AiNoteGenerationTimingContext,
} from '../../lib/aiNoteGenerationService';
import { getChimeSession } from '@unify/fhir-model/resources/appointment';
import {
  createTimingRecorder,
  createAiGenerationTraceId,
  type TimingRecorder,
} from '../../lib/timing/aiNotetakerTiming';
import { RescheduleRequestActions } from '../client-portal/components/RescheduleRequestActions';
import { appointmentNoteStatusLocksFulfillment, getVideoMeetingUrl } from '@unify/fhir-model/resources/appointment';
import { formatPractitionerPreferredLabel } from '@unify/fhir-model/resources/practitioner';
import { practitionerPreferredReference } from '../../lib/loadPractitionerPreferredLabels';
import { getPatientHomeState } from '@unify/fhir-model/resources/patient';
import { getCareManagerRef } from '@unify/fhir-model/resources/episode-of-care';
import { resolveAppointmentModality } from './utils/appointmentModality';
import {
  collectSessionRecordingPatientIds,
  haveAllPatientsConsentedToRecording,
} from '../../lib/sessionRecordingConsentIndicator';
import { AppointmentEditorModal } from '@/features/calendar/AppointmentEditorModal';
import {
  actorRefIsAppointmentParticipant,
  isClinicalNoteLockedStatus,
  shouldShowCalendarJoin,
} from '@/features/calendar/calendarEventGuards';
import { useAppointmentEditorTables } from '@/features/calendar/useAppointmentEditorTables';
import { useCalendarPracticeId } from '@/pages/calendar/appointmentTypeCatalog';
import {
  findEpisodeLinkedAppointments,
  type EpisodeLinkedAppointmentsResult,
} from './utils/episodeLinkedAppointments';
import {
  getParticipantAttendanceFromResponseItems,
  resolveParticipantAttendanceFromAppointmentParticipant,
} from './utils/participantAttendance';
import {
  commitSignedNoteAndSealAppointment,
  isSealPendingError,
  isSignedNoteLockError,
  persistNoteFormAttendance,
} from './utils/attendanceFulfillment';
import { notifySealPending } from './utils/sealPendingNotice';
import { PatientHeader } from './PatientHeader';
import { buildAppointmentDisplayTitle } from './utils/buildAppointmentDisplayTitle';
import { AppointmentTitleText } from './utils/AppointmentTitleText';
import { SessionMetadataUiProvider } from './components/SessionMetadataUiContext';
import { SessionToolsDrawer } from './components/SessionToolsDrawer';
import { useChimeInPersonRecording } from './hooks/useChimeInPersonRecording';
import { RecordingControlsTab } from '@/pages/session/tabs/RecordingControlsTab';
import { SessionClinicalPanelContent } from '@/pages/session/components/SessionClinicalPanelContent';
import { ProgressNoteCard } from './ProgressNoteCard';
import {
  useOptionalAiAssistant,
  useRegisterProgressNoteContext,
} from '@/features/ai-assistant';
import type { ProgressNoteContext } from '@/features/ai-assistant/types';
import { NoteAiToolbar } from './components/NoteAiToolbar';
import { TranscriptStatusBadge } from './components/TranscriptStatusBadge';
import { ConfirmAiNoteReadModal } from './components/ConfirmAiNoteReadModal';
import { SignatureCaptureModal } from '@/components/SignatureCapture';
import {
  buildNoteMetadataFromSources,
  getNoteMetadataFromQR,
  setNoteMetadataOnQR,
} from './noteMetadataMapping';
import {
  ProgressNoteForm,
  type AiNoteStatus,
  type ProgressNoteFormTextUndoApi,
  type ProgressNoteSaveOptions,
} from './ProgressNoteForm';
import {
  getDetailedStatus,
  setDetailedStatus,
  getAiNoteCheckResultsFromQR,
  mergeAcknowledgmentsOntoQR,
  practitionerRequiresCosignature,
  type AiNoteCheckAcknowledgment,
  getReviewCommentsFromQR,
  getRequestedEditsFromQR,
  getSignatureFromQR,
  pickPrimaryAppointmentNote,
  PROVIDER_SIGNATURE_EXT_URL,
  SUPERVISOR_SIGNATURE_EXT_URL,
  removeRequestedEditsFromQR,
  setReviewCommentsOnQR,
  setSignatureOnQR,
  type SignatureInfo,
} from './fhirNoteMapping';
import type { Addendum, AddendumLineChange, AddendumReasonCode } from './addendums';
import {
  AddendumReasonModal,
  getMockAddendumsForAppointment,
  computeAddendumLineChanges,
  extractQRTextLines,
  loadAddendumsForAppointment,
  documentReferencesToAddendums,
  documentReferenceToAddendum,
  createAddendum,
} from './addendums';
import { isMockQuestionnaireResponseId, getMockNoteEntryForAppointment } from './mockAppointmentNoteData';
import { isExemplarNote, setExemplarNoteTag } from '@unify/fhir-model/resources/questionnaire-response';
import { useAppointmentResponses, useAppointmentNoteSources, type AppointmentResponseEntry } from './hooks';
import { getMockAppointmentPageStateByAppointmentId, type AppointmentPageMockState } from './mockAppointmentPageData';
import {
  getAppointmentWorkflowStatusBadgeColor,
  getAppointmentWorkflowStatusLabel,
} from './utils/appointmentWorkflowStatus';
import { isCalendarClientAttendanceResolved } from '@/lib/calendarClientAttendanceStatus';
import { getAppointmentServiceTypeLabel } from './normalizeServiceType';
import {
  enrichAppointmentAttendeeDisplayNames,
  getAllPatientParticipantsFromAppointment,
  getPatientIdFromAppointment,
  getPractitionerIdFromAppointment,
  findEncounterForAppointment,
  loadQuestionnaireForNote,
  patientStubFromAppointmentParticipant,
  practitionerDisplayLabelFromAppointment,
} from './utils/fhirHelpers';
import type { AutomaticReviewRunState } from '@/components/NoteQualityChecklist';
import { PsychotherapyNoteSection } from '@/components/psychotherapy/PsychotherapyNoteSection';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import type { LinkedAppointmentInfo } from './types';

/** Refetch appointment notes while AI generation is in flight (WS + cache can miss the final QR update). */
const AI_DRAFT_POLL_INTERVAL_MS = 3_000;

/** Safety net: clear awaitingAiDraft if the bot never reaches a terminal QR status. */
const AI_AWAITING_DRAFT_TIMEOUT_MS = 6.5 * 60_000;

/** Re-check the transcript status while the transcript/note pipeline is still pending. */
const TRANSCRIPT_STATUS_POLL_INTERVAL_MS = 60_000;

type NoteMode = 'view' | 'add' | 'edit';

// ---------------------------------------------------------------------------
// Group session edit modal helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Review safeguard constants
// ---------------------------------------------------------------------------

/** After AI Generate Note is clicked, require confirm-read modal if save is within this window. */
const AI_NOTETAKER_REVIEW_WINDOW_MS = 120_000; // 2 minutes

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

const MOCK_STATE_KEY = 'mockAppointmentData';

export function AppointmentPage(): JSX.Element {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [searchParams] = useSearchParams();
  /** Set when opening a cohort session from a client chart — select that patient's fan-out note. */
  const chartPatientId = searchParams.get('patientId')?.trim() || undefined;
  const location = useLocation();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { role, availableRoles } = usePermissions();
  /** A user can hold both admin and clinical roles; clinical authorship follows the whole set. */
  const clinicalRoles = useMemo(
    () => [...new Set<Role>([role, ...availableRoles])],
    [role, availableRoles],
  );
  const canManageExemplarNotes = role === 'unify-admin';
  const { practitioner, loading: practitionerLoading } = useCurrentUserPractitioner();
  const { ids: supervisedPractitionerIds, loading: superviseesLoading } = useSupervisedPractitionerIds();
  const organizationId = useCalendarPracticeId(practitioner ?? undefined);
  const viewScope = useMemo(() => getViewScope(role), [role]);
  const appointmentAccessCtx = useMemo(
    () =>
      chartScopeFromRoleAndPractitioner(
        role,
        practitioner?.id,
        supervisedPractitionerIds,
      ),
    [role, practitioner?.id, supervisedPractitionerIds],
  );

  const mockState = (location.state as { [MOCK_STATE_KEY]?: AppointmentPageMockState } | null)?.[MOCK_STATE_KEY];

  // Primary data
  const [appointment, setAppointment] = useState<Appointment | null>(mockState?.appointment ?? null);
  const [patient, setPatient] = useState<Patient | null>(mockState?.patient ?? null);
  const [appointmentPractitioner, setAppointmentPractitioner] = useState<Practitioner | null>(null);
  const [encounterId, setEncounterId] = useState<string | undefined>(undefined);

  // Linked appointments
  const [previousAppt, setPreviousAppt] = useState<LinkedAppointmentInfo | null>(
    mockState?.previousAppointment ?? null,
  );
  const [nextAppt, setNextAppt] = useState<LinkedAppointmentInfo | null>(mockState?.nextAppointment ?? null);
  const [editAppointmentOpen, setEditAppointmentOpen] = useState(false);
  const [sessionToolsOpen, setSessionToolsOpen] = useState(false);

  /**
   * Clear neighbour links when the route appointment changes; re-resolved by loadAppointmentData.
   * Prev/next links still carry `appointmentSequenceNumber` in navigation state for history.
   */
  useEffect(() => {
    setPreviousAppt(null);
    setNextAppt(null);
  }, [appointmentId]);

  // Loading & error
  const [loading, setLoading] = useState(!mockState);
  const [error, setError] = useState<string | null>(null);

  /** Binary header badge: all Patient participants have a permitted recording Consent.
   * null while loading or when not applicable (no patients). */
  const [sessionRecordingConsented, setSessionRecordingConsented] = useState<boolean | null>(null);
  useEffect(() => {
    setSessionRecordingConsented(null);
    if (!appointment) return undefined;
    const modality = resolveAppointmentModality(appointment);
    if (modality !== 'telehealth' && modality !== 'hybrid' && modality !== 'in-person') {
      return undefined;
    }
    const patientIds = collectSessionRecordingPatientIds({
      attendeeRefs: (appointment.participant ?? [])
        .map((p) => p.actor?.reference)
        .filter((r): r is string => Boolean(r)),
    });
    if (patientIds.length === 0) return undefined;
    let cancelled = false;
    void haveAllPatientsConsentedToRecording(medplum, patientIds).then((allPermitted) => {
      if (!cancelled) setSessionRecordingConsented(allPermitted);
    });
    return () => {
      cancelled = true;
    };
  }, [appointment, medplum]);

  // Supervisor
  const [hasSupervisor, setHasSupervisor] = useState(mockState?.hasSupervisor ?? false);

  /** Episode of care label for header (from mock state or fallback). */
  const [episodeDisplayName, setEpisodeDisplayName] = useState<string | undefined>(mockState?.episodeDisplayName);

  /** Loaded for RBAC when not using demo/mock Medplum data. */
  const [episodeOfCareForAccess, setEpisodeOfCareForAccess] = useState<EpisodeOfCare | null>(null);

  // --- Note mode (view / add / edit) ---
  const [noteMode, setNoteMode] = useState<NoteMode>('view');

  // --- Addendums: persisted in Medplum as DocumentReferences ---
  const [persistedAddendums, setPersistedAddendums] = useState<Addendum[]>([]);
  /** Optimistic addendums: shown immediately after save, before Medplum round-trip completes. */
  const [optimisticAddendums, setOptimisticAddendums] = useState<Addendum[]>([]);

  const loadPersistedAddendums = useCallback(async () => {
    if (!appointmentId || !patient?.id) return;
    try {
      const docRefs = await loadAddendumsForAppointment(medplum, patient.id, appointmentId);
      setPersistedAddendums(documentReferencesToAddendums(docRefs));
      setOptimisticAddendums([]);
    } catch (err) {
      console.warn('Could not load persisted addendums:', err);
    }
  }, [medplum, appointmentId, patient?.id]);

  useEffect(() => {
    setPersistedAddendums([]);
    setOptimisticAddendums([]);
  }, [appointmentId]);

  useEffect(() => {
    loadPersistedAddendums();
  }, [loadPersistedAddendums]);

  /** Revise Note: reason modal after quality checks, before re-sign. */
  const [addendumReasonModalOpen, setAddendumReasonModalOpen] = useState(false);
  const addendumReasonWaitRef = useRef<{
    resolve: (value: { reason: AddendumReasonCode; comments: string } | null) => void;
  } | null>(null);
  const pendingAddendumAfterSignRef = useRef<{
    lineChanges: AddendumLineChange[];
    nextNumber: number;
    originalLines: string[];
    afterLines: string[];
    reason: AddendumReasonCode;
    comments: string;
  } | null>(null);

  // --- QuestionnaireResponse data from Medplum ---
  const {
    entries: responseEntries,
    loading: responsesLoading,
    refetch: refetchResponses,
    replaceResponse,
  } = useAppointmentResponses(appointmentId, appointment);

  /** Track locally saved note entry so it displays immediately without requiring a server round-trip. */
  const [localNoteEntry, setLocalNoteEntry] = useState<AppointmentResponseEntry | null>(null);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [awaitingAiDraft, setAwaitingAiDraft] = useState(false);
  const awaitingAiDraftRef = useRef(false);
  const [revisionLoading, setRevisionLoading] = useState(false);
  const aiTimingRef = useRef<TimingRecorder | null>(null);
  const aiCompletionNotifiedRef = useRef<string | null>(null);

  const [aiCheckState, setAiCheckState] = useState<AutomaticReviewRunState>('not_started');
  const [aiCheckError, setAiCheckError] = useState<string | null>(null);

  const aiAccess = useGeminiAiAccess(patient?.id, practitioner?.id);
  const geminiAiDisabled = aiAccess.geminiAiDisabled || !aiAccess.transcriptsAllowed;

  // One note per appointment: use locally saved response, Medplum response, or mock note when no server data.
  // Cohort Group Therapy: chart ?patientId → that client's fan-out copy; otherwise the shared Group note.
  const { serverPrimaryEntry, cohortNoteKind } = useMemo((): {
    serverPrimaryEntry: AppointmentResponseEntry | undefined;
    cohortNoteKind: CohortNoteKind | undefined;
  } => {
    if (appointment && isCohortNoteContext(appointment, responseEntries)) {
      const selected = selectCohortAppointmentNote(
        responseEntries,
        appointment,
        chartPatientId,
        pickPrimaryAppointmentNote,
      );
      return { serverPrimaryEntry: selected.entry, cohortNoteKind: selected.noteKind };
    }
    return {
      serverPrimaryEntry: pickPrimaryAppointmentNote(responseEntries),
      cohortNoteKind: undefined,
    };
  }, [appointment, responseEntries, chartPatientId]);

  const isIndividualCohortNote = cohortNoteKind === 'individual';
  const sharedSessionNoteHref =
    isIndividualCohortNote && appointmentId
      ? `/dashboard/appointments/${appointmentId}`
      : undefined;
  const aiWorkflowActive = generationLoading || awaitingAiDraft || revisionLoading;
  const preferServerPrimary = useMemo(() => {
    if (!serverPrimaryEntry) return aiWorkflowActive;
    const ds = getDetailedStatus(serverPrimaryEntry.response);
    if (aiWorkflowActive) return true;
    return ds === 'generation-in-progress';
  }, [serverPrimaryEntry, aiWorkflowActive]);

  const mockNoteEntry: AppointmentResponseEntry | null =
    appointmentId && !responsesLoading && responseEntries.length === 0 && !localNoteEntry && getMockAppointmentPageStateByAppointmentId(appointmentId)
      ? getMockNoteEntryForAppointment(appointmentId)
      : null;
  const currentEntry: AppointmentResponseEntry | undefined =
    preferServerPrimary && serverPrimaryEntry
      ? serverPrimaryEntry
      : localNoteEntry ?? serverPrimaryEntry ?? mockNoteEntry ?? undefined;
  const currentResponse = currentEntry?.response ?? null;
  const currentQuestionnaire = currentEntry?.questionnaire ?? null;

  const aiCheckPayload = currentResponse ? getAiNoteCheckResultsFromQR(currentResponse) : undefined;
  /** Post-autosave bot checks — still shown on signed note view. */
  const serverChecks = useMemo(
    () =>
      aiCheckPayload?.status === 'completed' || aiCheckPayload?.checks?.length
        ? aiCheckPayload?.checks
        : undefined,
    [aiCheckPayload?.status, aiCheckPayload?.checks],
  );

  const { scheduleAiNoteCheck } = useDebouncedAiNoteCheck({
    medplum,
    onRunning: () => {
      setAiCheckError(null);
      setAiCheckState('running');
    },
    onCompleted: (checked) => {
      if (checked.id) {
        replaceResponse(checked.id, checked);
        setLocalNoteEntry((prev) => {
          if (prev && prev.response.id === checked.id) {
            return { ...prev, response: checked };
          }
          return prev;
        });
      }
      setAiCheckState('completed');
      setAiCheckError(null);
      void refetchResponses();
    },
    onUnavailable: () => {
      setAiCheckState('not_started');
    },
    onFailed: (message) => {
      setAiCheckState('failed');
      setAiCheckError(message);
    },
  });

  useEffect(() => {
    if (aiCheckState === 'running') return;
    const payload = currentResponse ? getAiNoteCheckResultsFromQR(currentResponse) : undefined;
    if (!payload) {
      if (aiCheckState !== 'failed' || !aiCheckError) {
        setAiCheckState('not_started');
      }
      return;
    }
    if (payload.status === 'completed') {
      setAiCheckState('completed');
      setAiCheckError(null);
    } else if (payload.status === 'failed' && payload.checks?.length) {
      setAiCheckState('completed');
      setAiCheckError(null);
    } else if (payload.status === 'failed') {
      setAiCheckState('failed');
      setAiCheckError(payload.error ?? null);
    }
  }, [currentResponse, aiCheckState, aiCheckError]);

  // No note when neither local, Medplum, nor mock note is shown.
  // Only treat loading as blocking on the first fetch — background refetches must not unmount the editor.
  // Chart patient context: "no note" means no fan-out copy for that client (shared may still exist).
  const responsesInitiallyLoading = responsesLoading && responseEntries.length === 0;
  const hasNoNote = isIndividualCohortNote
    ? !responsesInitiallyLoading && !serverPrimaryEntry && !mockNoteEntry && !localNoteEntry
    : !responsesInitiallyLoading && responseEntries.length === 0 && !mockNoteEntry && !localNoteEntry;

  /** Sync episode title for header: mock navigation state, loaded EpisodeOfCare, or appointment service type. */
  useEffect(() => {
    if (mockState) {
      if (mockState.episodeDisplayName) {
        setEpisodeDisplayName(mockState.episodeDisplayName);
      }
      return;
    }
    if (episodeOfCareForAccess) {
      const t = episodeOfCareForAccess.type?.[0];
      const name =
        t?.text ??
        t?.coding?.find((c) => c.system === THERAPY_SERVICE_TYPE_SYSTEM)?.display ??
        t?.coding?.[0]?.display;
      setEpisodeDisplayName(name);
      return;
    }
    if (appointment) {
      const fromAppt =
        appointment.serviceType?.[0]?.text ??
        appointment.serviceType?.[0]?.coding?.[0]?.display ??
        appointment.appointmentType?.text ??
        appointment.appointmentType?.coding?.[0]?.display;
      setEpisodeDisplayName(fromAppt);
      return;
    }
    setEpisodeDisplayName(undefined);
  }, [mockState, episodeOfCareForAccess, appointment]);

  // Patient reference for QR linking
  const patientRef = patient?.id ? `Patient/${patient.id}` : undefined;
  const encounterRef = encounterId ? `Encounter/${encounterId}` : undefined;

  const [hasTranscript, setHasTranscript] = useState(false);
  const textUndoApiRef = useRef<ProgressNoteFormTextUndoApi | null>(null);

  const appointmentStatus = appointment?.status;
  const appointmentEnd = appointment?.end;
  const sessionEnded = useMemo(() => {
    if (!appointmentStatus) return false; // Appointment.status is required, so this means "no appointment loaded"
    return (
      appointmentStatus === 'fulfilled' ||
      (appointmentEnd ? Date.parse(appointmentEnd) < Date.now() : false)
    );
  }, [appointmentStatus, appointmentEnd]);

  const episodeOfCareIdForSources =
    episodeOfCareForAccess?.id ??
    appointment?.extension
      ?.find((ex) => ex.url === EPISODE_OF_CARE_CONTEXT_EXTENSION)
      ?.valueReference?.reference?.replace('EpisodeOfCare/', '');

  const {
    encounter: noteEncounter,
    chargeItems: noteChargeItems,
    episodeOfCare: noteEpisodeOfCare,
    refetch: refetchNoteSources,
  } = useAppointmentNoteSources(encounterId, episodeOfCareIdForSources);

  const sessionTiming = useMemo(
    () => readEncounterSessionTiming(noteEncounter),
    [noteEncounter],
  );

  useEffect(() => {
    setEncounterId(undefined);
    setHasTranscript(false);
  }, [appointmentId]);

  useEffect(() => {
    if (mockState) return;
    setHasTranscript(isSessionTranscriptReady(appointment ?? undefined));
  }, [appointment, mockState]);

  const transcriptStatusPending = shouldPollTranscriptStatus({
    appointment: appointment ?? undefined,
    sessionEnded,
  });
  const chimeMeetingId = appointment ? getChimeSession(appointment)?.meetingId : undefined;
  // The chime bot only answers participants; supervisors and other viewers read the Appointment.
  const viewerIsChimeParticipant = Boolean(
    practitioner?.id
    && appointment
    && actorRefIsAppointmentParticipant(appointment.participant, `Practitioner/${practitioner.id}`),
  );

  /**
   * Non-blocking transcript status check after the session ends (does not block first paint),
   * repeated while the transcript/note pipeline is still pending.
   */
  useEffect(() => {
    if (!appointmentId || mockState || !transcriptStatusPending) return;
    if (!chimeMeetingId) return;

    let cancelled = false;
    const check = async (): Promise<void> => {
      try {
        let result: { appointment?: Appointment };
        if (viewerIsChimeParticipant) {
          result = await requestChimeTranscriptStatusCheck(medplum, appointmentId);
        } else {
          result = {
            appointment: await medplum.readResource('Appointment', appointmentId, { cache: 'no-cache' }),
          };
        }
        if (cancelled || !result.appointment) return;
        setAppointment(result.appointment);
        const nowReady = isSessionTranscriptReady(result.appointment);
        setHasTranscript(nowReady);
        if (nowReady) {
          refetchNoteSources();
          void refetchResponses();
        }
      } catch (err) {
        console.warn('Transcript status check failed', err);
      }
    };

    void check();
    const interval = setInterval(() => void check(), TRANSCRIPT_STATUS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [appointmentId, mockState, transcriptStatusPending, chimeMeetingId, viewerIsChimeParticipant, medplum, refetchNoteSources, refetchResponses]);

  const refreshAppointmentFromServer = useCallback(async () => {
    if (!appointmentId || mockState) return;
    try {
      // Bypass Medplum client cache — participants and session extensions are written by bots
      // after a calendar/roster search may have already cached this Appointment.
      const fresh = await medplum.readResource('Appointment', appointmentId, {
        cache: 'no-cache',
      });
      setAppointment(fresh);
      refetchNoteSources();
      setHasTranscript(isSessionTranscriptReady(fresh));
    } catch {
      setHasTranscript(false);
    }
  }, [medplum, appointmentId, mockState, refetchNoteSources]);

  const appointmentSubscriptionCriteria = useMemo(
    () => (appointmentId ? buildAppointmentByIdSubscriptionCriteria(appointmentId) : undefined),
    [appointmentId],
  );

  useMedplumSubscriptionRefresh(appointmentSubscriptionCriteria, refreshAppointmentFromServer, {
    enabled: Boolean(appointmentId) && !mockState,
    debugLabel: '[Appointment][WS]',
  });

  const noteResponseSubscriptionCriteria = useMemo(() => {
    if (encounterId) {
      return buildEncounterQuestionnaireResponseSubscriptionCriteria(encounterId);
    }
    if (patient?.id) {
      return buildPatientQuestionnaireResponseSubscriptionCriteria(patient.id);
    }
    return undefined;
  }, [encounterId, patient?.id]);

  useMedplumSubscriptionRefresh(noteResponseSubscriptionCriteria, () => void refetchResponses(), {
    enabled: Boolean(noteResponseSubscriptionCriteria) && !mockState,
    debugLabel: '[AppointmentNote][WS]',
  });

  useEffect(() => {
    awaitingAiDraftRef.current = awaitingAiDraft;
  }, [awaitingAiDraft]);

  /** Poll while waiting for bot to finish — WS may miss updates; refetch bypasses stale search cache. */
  useEffect(() => {
    if (!awaitingAiDraft || mockState) return;
    const interval = setInterval(() => {
      void refetchResponses();
    }, AI_DRAFT_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [awaitingAiDraft, mockState, refetchResponses]);

  /** If generation never reaches ai-draft / generation-failed, unblock the UI. */
  useEffect(() => {
    if (!awaitingAiDraft || mockState) return;
    const timer = setTimeout(() => {
      setAwaitingAiDraft(false);
      setGenerationLoading(false);
      notify({
        type: 'error',
        title: 'Generation timed out',
        message: 'AI note generation took too long. Try again or write the note manually.',
      });
    }, AI_AWAITING_DRAFT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [awaitingAiDraft, mockState]);

  const aiNoteStatus: AiNoteStatus = useMemo(() => {
    if (mockState) return null;
    const ds = currentResponse ? getDetailedStatus(currentResponse) : undefined;
    if (ds === 'generation-failed') return 'generation-failed';
    if (generationLoading || awaitingAiDraft || revisionLoading || ds === 'generation-in-progress') {
      return 'generating';
    }
    if (ds === 'ai-draft') return 'ai-draft';
    if (currentResponse) return null;
    if (!encounterRef) return null;
    if (!hasTranscript && !currentResponse) return 'transcript-pending';
    return null;
  }, [
    mockState,
    currentResponse,
    encounterRef,
    generationLoading,
    awaitingAiDraft,
    revisionLoading,
    hasTranscript,
  ]);

  useEffect(() => {
    if (!currentResponse) return;
    const ds = getDetailedStatus(currentResponse);
    if (ds !== 'ai-draft' && ds !== 'generation-failed') return;

    const wasAwaiting = awaitingAiDraftRef.current;
    setAwaitingAiDraft(false);

    if (wasAwaiting && aiTimingRef.current) {
      aiTimingRef.current.checkpoint(
        ds === 'ai-draft' ? 'ai_note_rendered_at' : 'ai_generation_failed_rendered_at',
        { questionnaireResponseId: currentResponse.id },
      );
      aiTimingRef.current = null;
    }

    if (wasAwaiting) {
      setLocalNoteEntry(null);
      setNoteMode((mode) => (mode !== 'edit' ? 'edit' : mode));
      if (ds === 'ai-draft') {
        textUndoApiRef.current?.remountForm();
        const notifyKey = `${currentResponse.id}:${currentResponse.meta?.versionId ?? ''}`;
        if (aiCompletionNotifiedRef.current !== notifyKey) {
          aiCompletionNotifiedRef.current = notifyKey;
          notify({
            type: 'success',
            title: 'AI note ready',
            message: 'Review the generated draft before signing.',
          });
        }
      } else {
        const notifyKey = `failed:${currentResponse.id}`;
        if (aiCompletionNotifiedRef.current !== notifyKey) {
          aiCompletionNotifiedRef.current = notifyKey;
          notify({
            type: 'error',
            title: 'AI note generation failed',
            message: 'Try again or write the note manually.',
          });
        }
      }
    } else if (noteMode === 'view') {
      setNoteMode('edit');
    }
// eslint-disable-next-line react-hooks/exhaustive-deps -- effect is keyed on the response id/versionId; depending on the whole currentResponse object would re-run the AI-draft completion handling (remount + toast) on every unrelated response update
  }, [responseEntries, currentResponse?.id, currentResponse?.meta?.versionId, noteMode]);

  useEffect(() => {
    if (!awaitingAiDraft || !currentResponse) return;
    if (getDetailedStatus(currentResponse) === 'generation-in-progress') {
      textUndoApiRef.current?.remountForm();
    }
// eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on response id/versionId so the form remounts once per generation update, not on every currentResponse object change
  }, [awaitingAiDraft, currentResponse?.id, currentResponse?.meta?.versionId]);

  /** Scroll to top when opening or switching to this appointment. */
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [appointmentId]);

  /** Reset note editor state when navigating to a different appointment or chart patient context. */
  useEffect(() => {
    setNoteMode('view');
    setLocalNoteEntry(null);
    setAwaitingAiDraft(false);
    aiCompletionNotifiedRef.current = null;
  }, [appointmentId, chartPatientId]);

  const appointmentOnlyEarly =
    mockState?.appointmentOnly === true || mockState?.documentationOnly === true;

  /** New-note flow: open the editor in add mode instead of relying on hasNoNote alone (which clears after first autosave). */
  useEffect(() => {
    // Individual chart context must not auto-create the shared Group note.
    if (isIndividualCohortNote) return;
    if (appointmentOnlyEarly || responsesInitiallyLoading || !hasNoNote) return;
    setNoteMode((prev) => (prev === 'view' ? 'add' : prev));
  }, [appointmentOnlyEarly, responsesInitiallyLoading, hasNoNote, isIndividualCohortNote]);

  /** Reset persisted duration overrides when navigating to a different appointment. */
  useEffect(() => {
    setSessionDurationLocal(undefined);
    setAppointmentPractitioner(null);
  }, [appointmentId]);

  const editorTables = useAppointmentEditorTables({
    appointment: appointment ?? undefined,
    enabled: Boolean(appointment?.id),
    encounterId,
    episodeOfCareId: episodeOfCareIdForSources,
    subjectPatientId: patient?.id,
    questionnaireResponse: currentResponse,
    onAppointmentChange: setAppointment,
    onQuestionnaireResponseChange: (saved) => {
      if (!saved.id) return;
      replaceResponse(saved.id, saved);
      setLocalNoteEntry((prev) =>
        prev && prev.response.id === saved.id ? { ...prev, response: saved } : prev,
      );
    },
  });

  const loadAppointmentData = useCallback(async () => {
    if (!appointmentId) {
      setError('No appointment ID provided');
      setLoading(false);
      return;
    }

    try {
      setEncounterId(undefined);
      // Bypass Medplum client cache — a preceding Appointment search (group roster, calendar)
      // caches each result under its read URL, so a roster change synced by the group bots
      // would otherwise show stale participants until the cache expires.
      const appt = await medplum.readResource('Appointment', appointmentId, {
        cache: 'no-cache',
      });
      setAppointment(appt);

      /** Chart ?patientId wins for cohort sessions opened from a client chart. */
      const provisionalPatientId = getPatientIdFromAppointment(appt);

      /**
       * Episode sequencing needs a search across the episode. Kick it off from the appointment's
       * own episode extension so it runs alongside the reads below instead of after them —
       * otherwise "Appointment #N" lands a beat after the rest of the page.
       */
      const provisionalEpisodeId = appt.extension
        ?.find((ex) => ex.url === EPISODE_OF_CARE_CONTEXT_EXTENSION)
        ?.valueReference?.reference?.replace('EpisodeOfCare/', '');
      const provisionalLinkedPatientId = chartPatientId ?? provisionalPatientId;
      const provisionalLinkedPromise =
        provisionalLinkedPatientId && provisionalEpisodeId
          ? findEpisodeLinkedAppointments(
            medplum,
            provisionalLinkedPatientId,
            appointmentId,
            provisionalEpisodeId,
          )
          : null;

      const encounter = await findEncounterForAppointment(
        medplum,
        appointmentId,
        chartPatientId ?? provisionalPatientId,
        appt.start,
      );
      if (encounter?.id) {
        setEncounterId(encounter.id);
      } else {
        console.warn('[AppointmentPage] No Encounter linked to appointment', { appointmentId });
      }

      const encounterPatientId = encounter?.subject?.reference?.startsWith('Patient/')
        ? encounter.subject.reference.replace(/^Patient\//, '')
        : undefined;
      const patientIdToLoad = chartPatientId ?? encounterPatientId ?? provisionalPatientId;

      if (patientIdToLoad) {
        try {
          const patientResource = await medplum.readResource('Patient', patientIdToLoad);
          setPatient(patientResource);
        } catch {
          console.warn('Could not load patient:', patientIdToLoad);
          setPatient(patientStubFromAppointmentParticipant(appt, patientIdToLoad));
        }
      }

      const practitionerId = getPractitionerIdFromAppointment(appt);
      if (practitionerId) {
        try {
          const pract = await medplum.readResource('Practitioner', practitionerId);
          setAppointmentPractitioner(pract);
          setHasSupervisor(practitionerRequiresCosignature(pract));
        } catch {
          setAppointmentPractitioner(null);
          console.warn('Could not load practitioner:', practitionerId);
        }
      } else {
        setAppointmentPractitioner(null);
      }

      // Load addendums using resolved patient id (state may not have updated yet this tick).
      if (patientIdToLoad) {
        try {
          const docRefs = await loadAddendumsForAppointment(
            medplum,
            patientIdToLoad,
            appointmentId,
          );
          setPersistedAddendums(documentReferencesToAddendums(docRefs));
          setOptimisticAddendums([]);
        } catch (e) {
          console.warn('Could not load addendums for appointment:', e);
        }
      }

      setEpisodeOfCareForAccess(null);
      const fromEnc = encounter?.episodeOfCare?.[0]?.reference?.replace('EpisodeOfCare/', '');
      const eocId = fromEnc || provisionalEpisodeId;
      if (eocId) {
        try {
          const eocRes = await medplum.readResource('EpisodeOfCare', eocId);
          setEpisodeOfCareForAccess(eocRes);
        } catch {
          setEpisodeOfCareForAccess(null);
        }
      }

      let linked: EpisodeLinkedAppointmentsResult | null = null;
      if (
        provisionalLinkedPromise
        && patientIdToLoad === provisionalLinkedPatientId
        && eocId === provisionalEpisodeId
      ) {
        linked = await provisionalLinkedPromise;
      } else if (patientIdToLoad && eocId) {
        linked = await findEpisodeLinkedAppointments(medplum, patientIdToLoad, appointmentId, eocId);
      }
      setPreviousAppt(linked?.previous ?? null);
      setNextAppt(linked?.next ?? null);

      setLoading(false);
    } catch (err) {
      console.error('Failed to load appointment:', err);
      setEpisodeOfCareForAccess(null);
      const fallback = appointmentId ? getMockAppointmentPageStateByAppointmentId(appointmentId) : null;
      if (fallback) {
        setAppointment(fallback.appointment);
        setPatient(fallback.patient);
        setPreviousAppt(fallback.previousAppointment);
        setNextAppt(fallback.nextAppointment);
        setHasSupervisor(fallback.hasSupervisor);
        setEpisodeDisplayName(fallback.episodeDisplayName);
        setError(null);
      } else {
        setError('Failed to load appointment details. The appointment may not exist or you may not have access.');
      }
      setLoading(false);
    }
  }, [medplum, appointmentId, chartPatientId]);

  useEffect(() => {
    if (mockState) {
      setAppointment(mockState.appointment);
      setPatient(mockState.patient);
      setPreviousAppt(mockState.previousAppointment);
      setNextAppt(mockState.nextAppointment);
      setHasSupervisor(mockState.hasSupervisor);
      setEpisodeDisplayName(mockState.episodeDisplayName);
      setEpisodeOfCareForAccess(null);
      setLoading(false);
      setError(null);
      // Chart navigation carries an Appointment synthesized from chart rows, which has no Group
      // link. For live appointments, load the real resource so cohort sessions resolve their
      // Group and the client's fan-out copy.
      const isMockAppointment = Boolean(
        appointmentId && getMockAppointmentPageStateByAppointmentId(appointmentId),
      );
      if (!chartPatientId || isMockAppointment) return;
    }
    loadAppointmentData();
  }, [loadAppointmentData, mockState, appointmentId, chartPatientId]);

  const profile = useMedplumProfile();

  // --- Signatures & edit/addendum gating ---
  const providerSignature = currentResponse
    ? getSignatureFromQR(currentResponse, PROVIDER_SIGNATURE_EXT_URL)
    : undefined;
  const supervisorSignature = currentResponse
    ? getSignatureFromQR(currentResponse, SUPERVISOR_SIGNATURE_EXT_URL)
    : undefined;

  /** Lock AI generation while the note carries a signature; supervisor "request edits" clears provider sig and re-enables. */
  const aiNotetakerLocked = useMemo(() => {
    if (!currentResponse) return false;
    if (getRequestedEditsFromQR(currentResponse)) return false;
    return Boolean(providerSignature || supervisorSignature);
  }, [currentResponse, providerSignature, supervisorSignature]);

  const signReviewWarning = useMemo(() => {
    if (providerSignature) return undefined;
    if (aiCheckState === 'running') {
      return 'Please wait for AI review before signing.';
    }
    if (aiCheckState === 'failed') {
      return 'Automatic review could not complete. Please manually review the note before signing.';
    }
    return undefined;
  }, [providerSignature, aiCheckState]);

  const detailedStatus = currentResponse ? getDetailedStatus(currentResponse) : undefined;
  const isLockedByDetailedStatus = isClinicalNoteLockedStatus(detailedStatus);
  const episodeCareManagerPractitionerId = useMemo(() => {
    const ref = episodeOfCareForAccess
      ? getCareManagerRef(episodeOfCareForAccess)?.reference
      : undefined;
    if (!ref?.startsWith('Practitioner/')) return undefined;
    return ref.slice('Practitioner/'.length);
  }, [episodeOfCareForAccess]);
  const appointmentPractitionerId = appointment
    ? getPractitionerIdFromAppointment(appointment)
    : undefined;
  /** Only the episode care manager (session provider) may fill out or sign the clinical note. */
  const isCareManager = isSessionCareManager({
    viewerPractitionerId: practitioner?.id,
    episodeCareManagerPractitionerId,
    appointmentPractitionerId,
  });
  const mayAuthorClinicalNote = canAuthorClinicalNoteForRoles(clinicalRoles) && isCareManager;
  const mayViewPsychotherapyNote = canViewPsychotherapyNote(role);
  /**
   * Signing is limited to the care manager who authored the note. Notes without an author
   * reference (new drafts) can be signed by the care manager, who becomes the author on sign.
   */
  const noteAuthorRef = currentResponse?.author?.reference;
  const isNoteAuthor = Boolean(
    profile?.id &&
      (!noteAuthorRef || noteAuthorRef === `${profile.resourceType}/${profile.id}`),
  );
  const mayReviseNote = mayAuthorClinicalNote && isNoteAuthor;
  /** Draft edit: only the care manager, and only while the note is unsigned. */
  const canEditDraft = mayAuthorClinicalNote && !providerSignature && !isLockedByDetailedStatus;
  const canSignNote = mayAuthorClinicalNote && isNoteAuthor;
  /** Addendum flow: available once the provider has signed (supervisor cosign not required). */
  const addendumFlowEligible = !!providerSignature && mayReviseNote;
  /** Recording/transcript state only matters while the note is still being written. */
  const noteHeaderBadge =
    providerSignature || isLockedByDetailedStatus ? undefined : (
      <TranscriptStatusBadge appointment={appointment ?? undefined} sessionEnded={sessionEnded} note={currentResponse ?? undefined} />
    );
  const originalNoteDate =
    currentResponse?.authored ??
    appointment?.start ??
    new Date().toISOString();
  const mockAddendumsRaw = getMockAddendumsForAppointment(appointmentId ?? undefined, originalNoteDate);
  const mockAddendums = mockAddendumsRaw;
  const allAddendumSources = [...persistedAddendums, ...optimisticAddendums, ...mockAddendums];
  const addendums: Addendum[] =
    allAddendumSources.length > 0
      ? allAddendumSources.sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      )
      : [];
  const profileDisplayName =
    profile?.name?.[0] &&
    [...(profile.name[0].given ?? []), profile.name[0].family].filter(Boolean).join(' ');
  const addendumAuthorName = providerSignature?.name ?? profileDisplayName ?? 'Provider';
  const addendumAuthorCredentials = providerSignature?.license;

  const preEditSnapshotRef = useRef<string[] | null>(null);
  const [isAddendumEdit, setIsAddendumEdit] = useState(false);
  /**
   * After sign, appointment participants + header actions stay locked until Revise Note
   * (addendum edit). Unsigned / no-note appointments remain editable.
   */
  const appointmentFieldsLocked =
    (Boolean(providerSignature) || isLockedByDetailedStatus) && !isAddendumEdit;

  useEffect(() => {
    setIsAddendumEdit(false);
  }, [appointmentId]);

  // --- Locked-note edit warning modal ---
  const [lockedNoteEditWarningOpen, { open: openLockedNoteEditWarning, close: closeLockedNoteEditWarning }] =
    useDisclosure(false);

  /**
   * Enter note editor. Only set `captureSnapshotForAddendum` when editing a fully signed note
   * so saves can create addendums; draft edits must not snapshot (no addendum before signing).
   */
  const enterEditMode = useCallback(
    (options?: { captureSnapshotForAddendum?: boolean }) => {
      setIsAddendumEdit(Boolean(options?.captureSnapshotForAddendum));
      if (options?.captureSnapshotForAddendum && currentResponse) {
        preEditSnapshotRef.current = extractQRTextLines(currentResponse, currentQuestionnaire);
      } else {
        preEditSnapshotRef.current = null;
      }
      setNoteMode('edit');
    },
    [currentResponse, currentQuestionnaire],
  );

  // --- Note action handlers ---

  const handleEditDraft = useCallback(() => {
    enterEditMode();
  }, [enterEditMode]);

  const handleAddendumEdit = useCallback(() => {
    openLockedNoteEditWarning();
  }, [openLockedNoteEditWarning]);

  const handleConfirmLockedNoteEdit = useCallback(() => {
    closeLockedNoteEditWarning();
    enterEditMode({ captureSnapshotForAddendum: true });
  }, [closeLockedNoteEditWarning, enterEditMode]);

  const handleCancelEdit = useCallback(() => {
    setIsAddendumEdit(false);
    preEditSnapshotRef.current = null;
    if (hasNoNote) {
      navigate(-1);
      return;
    }
    setNoteMode('view');
  }, [hasNoNote, navigate]);

  const maybePromptAddendumAfterEdit = useCallback(
    async (
      savedResponse: QuestionnaireResponse,
      resolvedQ: import('@medplum/fhirtypes').Questionnaire | null,
    ): Promise<boolean> => {
      const originalLines =
        preEditSnapshotRef.current
        ?? extractQRTextLines(savedResponse, resolvedQ);
      const afterLines = extractQRTextLines(savedResponse, resolvedQ);
      const lineChanges = computeAddendumLineChanges(originalLines, afterLines);
      const nextNumber = addendums.length + 1;

      const choice = await new Promise<{
        reason: AddendumReasonCode;
        comments: string;
      } | null>((resolve) => {
        addendumReasonWaitRef.current = { resolve };
        setAddendumReasonModalOpen(true);
      });

      if (!choice) {
        // Keep snapshot so Review & Sign can be retried.
        if (!preEditSnapshotRef.current) {
          preEditSnapshotRef.current = originalLines;
        }
        return false;
      }

      pendingAddendumAfterSignRef.current = {
        lineChanges,
        nextNumber,
        originalLines,
        afterLines,
        reason: choice.reason,
        comments: choice.comments,
      };
      preEditSnapshotRef.current = null;
      return true;
    },
    [addendums.length],
  );

  const handleBeforeResign = useCallback(
    async (savedResponse: QuestionnaireResponse | null): Promise<boolean> => {
      const qr = savedResponse ?? currentResponse;
      if (!qr) return false;
      return maybePromptAddendumAfterEdit(qr, currentQuestionnaire);
    },
    [currentResponse, currentQuestionnaire, maybePromptAddendumAfterEdit],
  );

  const handleSaveNote = useCallback(
    async (
      savedResponse: QuestionnaireResponse,
      options?: ProgressNoteSaveOptions,
    ) => {
      const saveReason = options?.reason ?? 'finish';
      // Update cache immediately so UI reflects saved response (e.g. when editing signed note)
      if (savedResponse.id) {
        replaceResponse(savedResponse.id, savedResponse);
      }

      const isMockSaved = isMockQuestionnaireResponseId(savedResponse.id);
      if (!isMockSaved && savedResponse.id) {
        scheduleAiNoteCheck(savedResponse);
      }

      // Bidirectional Group note sync: shared → individuals, or patient section → shared.
      if (
        !isMockSaved
        && savedResponse.id
        && appointment
        && isCohortGroupAppointment(appointment)
      ) {
        const subjectRef = savedResponse.subject?.reference ?? '';
        try {
          if (subjectRef.startsWith('Group/')) {
            await requestGroupSharedNoteFanout(
              medplum,
              savedResponse.id,
              appointment.id,
              { strict: false },
            );
          } else if (subjectRef.startsWith('Patient/')) {
            await requestGroupSharedNoteMergeFromPatient(
              medplum,
              savedResponse.id,
              appointment.id,
            );
          }
        } catch (syncErr) {
          // Don't block draft autosave when attendance isn't ready yet; warn on explicit saves.
          if (saveReason !== 'autosave') {
            console.warn('Group note sync failed:', syncErr);
            notify({
              type: 'warning',
              title: 'Group note sync pending',
              message:
                getUserFacingErrorMessage(syncErr, 'Could not sync shared and patient-specific notes yet.'),
            });
          } else {
            console.warn('Group note soft sync skipped:', syncErr);
          }
        }
      }

      if (saveReason === 'autosave') {
        if (!awaitingAiDraftRef.current) {
          setLocalNoteEntry((prev) => ({
            response: savedResponse,
            questionnaire: prev?.questionnaire ?? null,
          }));
        }
        return;
      }

      const isMock = isMockSaved;
      if (!isMock) {
        void refetchResponses();
      }

      // Resolve the questionnaire for the saved response (robust ref + search fallback for template labels)
      const rawQ = savedResponse.questionnaire;
      const qRef =
        typeof rawQ === 'string'
          ? rawQ
          : rawQ != null && typeof rawQ === 'object' && 'reference' in rawQ
            ? (rawQ as { reference: string }).reference
            : undefined;
      let resolvedQ: import('@medplum/fhirtypes').Questionnaire | null = qRef
        ? await loadQuestionnaireForNote(medplum, qRef)
        : null;
      if (!resolvedQ && qRef) {
        const { getMockAppointmentNoteTemplates } = await import('./mockAppointmentTemplates');
        const mockTemplates = getMockAppointmentNoteTemplates();
        const qId = qRef.match(/Questionnaire\/([^/?#]+)/)?.[1];
        resolvedQ =
          mockTemplates.find((t) => t.id === qId || t.questionnaire.id === qId)?.questionnaire ?? null;
      }
      setLocalNoteEntry({ response: savedResponse, questionnaire: resolvedQ });
    },
    [refetchResponses, replaceResponse, appointment, medplum, scheduleAiNoteCheck],
  );

  const handleFinishEdit = useCallback(() => {
    setNoteMode('view');
    setIsAddendumEdit(false);
  }, []);

  const handleAddendumReasonSubmit = useCallback(
    (reason: AddendumReasonCode, comments: string) => {
      const waiter = addendumReasonWaitRef.current;
      addendumReasonWaitRef.current = null;
      setAddendumReasonModalOpen(false);
      waiter?.resolve({ reason, comments });
    },
    [],
  );

  const handleAddendumReasonClose = useCallback(() => {
    const waiter = addendumReasonWaitRef.current;
    addendumReasonWaitRef.current = null;
    setAddendumReasonModalOpen(false);
    waiter?.resolve(null);
  }, []);

  const createPendingAddendumAfterSign = useCallback(async (): Promise<void> => {
    const pending = pendingAddendumAfterSignRef.current;
    pendingAddendumAfterSignRef.current = null;
    if (!pending) return;

    const {
      lineChanges,
      nextNumber,
      originalLines,
      afterLines,
      reason,
      comments,
    } = pending;
    const now = new Date().toISOString();
    const body = comments.trim();
    const optimisticId = `optimistic-${Date.now()}`;

    const optimisticAddendum: Addendum = {
      id: optimisticId,
      label: `Revised Note #${nextNumber}`,
      authorName: addendumAuthorName,
      authorCredentials: addendumAuthorCredentials,
      timestamp: now,
      originalNoteDate,
      body,
      reasonForChange: reason,
      lineChanges,
      originalLines,
      afterLines,
    };

    setOptimisticAddendums((prev) => [...prev, optimisticAddendum]);

    const isMock = isMockQuestionnaireResponseId(currentResponse?.id);
    if (!isMock && currentResponse?.id && patient?.id && appointmentId) {
      try {
        const practitioner = await resolveCurrentUserPractitioner(medplum, profile);
        const authorRef = practitioner?.id ? `Practitioner/${practitioner.id}` : undefined;
        const practitionerDisplay =
          practitioner ? formatPractitionerPreferredLabel(practitioner) || undefined : undefined;

        const created = await createAddendum(medplum, {
          patientId: patient.id,
          appointmentId,
          questionnaireResponseId: currentResponse.id,
          encounterId,
          authorRef,
          authorDisplay: practitionerDisplay,
          body,
          reasonForChange: reason,
          sequenceNumber: nextNumber,
          authorName: addendumAuthorName,
          authorCredentials: addendumAuthorCredentials,
          originalNoteDate,
          lineChanges,
          originalLines,
          afterLines,
        });
        const persistedUi = documentReferenceToAddendum(created);
        setOptimisticAddendums((prev) => prev.filter((a) => a.id !== optimisticId));
        setPersistedAddendums((prev) => {
          const id = persistedUi.id;
          const rest = prev.filter((a) => a.id !== id);
          return [...rest, persistedUi].sort(
            (a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
          );
        });
      } catch (err) {
        console.error('Failed to persist addendum:', err);
        setOptimisticAddendums((prev) => prev.filter((a) => a.id !== optimisticId));
        notify({
          title: 'Revised note not saved',
          error: err,
          message: 'Could not save the revised note to the server. Your changes to the note may still be saved.',
          type: 'error',
        });
      }
    }
  }, [
    addendumAuthorName,
    addendumAuthorCredentials,
    originalNoteDate,
    currentResponse,
    patient?.id,
    appointmentId,
    encounterId,
    medplum,
    profile,
  ]);

  /** Resolves true when the note is signed; false when signing failed (modal stays open). */
  const handleSignNote = useCallback(
    async (signature: SignatureInfo, acknowledgments?: AiNoteCheckAcknowledgment[]): Promise<boolean> => {
      if (!currentResponse?.id) return false;
      if (!isCareManager) {
        throw new UserFacingError('Only the session care manager can sign this note.');
      }
      if (!isNoteAuthor) {
        throw new UserFacingError('Only the provider who created this note can sign it.');
      }
      if (isIndividualCohortNote) {
        throw new UserFacingError(
          'Individual Group Therapy notes cannot be signed. Sign the shared session note instead.',
        );
      }
      try {
        let updated = setSignatureOnQR(currentResponse, PROVIDER_SIGNATURE_EXT_URL, signature);
        updated = removeRequestedEditsFromQR(updated);
        if (acknowledgments?.length) {
          updated = mergeAcknowledgmentsOntoQR(updated, acknowledgments);
        }
        if (!updated.author?.reference && profile?.resourceType === 'Practitioner') {
          updated = { ...updated, author: practitionerPreferredReference(profile) };
        }

        const metadataSnapshot =
          getNoteMetadataFromQR(updated) ??
          (appointment
            ? buildNoteMetadataFromSources({
                appointment,
                encounter: noteEncounter,
                chargeItems: noteChargeItems,
                episodeOfCare: noteEpisodeOfCare,
              })
            : undefined);
        if (metadataSnapshot) {
          updated = setNoteMetadataOnQR(updated, metadataSnapshot);
        }

        const isMock = isMockQuestionnaireResponseId(currentResponse.id);
        const isSharedGroupNote =
          Boolean(appointment && isCohortGroupAppointment(appointment))
          && Boolean(updated.subject?.reference?.startsWith('Group/'));

        // Attendance lives on the note. Copy it to the Appointment before the note
        // is signed, for every clinical appointment — not only shared group notes.
        // Fan-out still reads Appointment participant extensions (PRD §11).
        const noteAttendance = getParticipantAttendanceFromResponseItems(updated.item);
        if (!isMock && appointment?.id) {
          const fromQr = noteAttendance;
          if (isSharedGroupNote && currentResponse.id) {
            const unresolvedIds: string[] = [];
            for (const p of appointment.participant ?? []) {
              const ref = p.actor?.reference?.trim();
              if (!ref?.startsWith('Patient/')) continue;
              const id = ref.slice('Patient/'.length).trim();
              if (!id) continue;
              const status =
                fromQr[id] ??
                resolveParticipantAttendanceFromAppointmentParticipant(p, appointment);
              if (!isCalendarClientAttendanceResolved(status)) {
                unresolvedIds.push(id);
              }
            }
            if (unresolvedIds.length > 0) {
              const message =
                'Set each participant to Completed, No show, or Cancelled before signing. Confirmed / Requested are not final attendance.';
              notify({
                type: 'error',
                title: 'Cannot finalize shared note',
                message,
              });
              throw new UserFacingError(message);
            }
          }

          try {
            const savedAppt = await persistNoteFormAttendance(medplum, appointment, fromQr);
            if (
              savedAppt.status !== appointment.status
              || JSON.stringify(savedAppt.participant) !== JSON.stringify(appointment.participant)
            ) {
              setAppointment(savedAppt);
            }
          } catch (err) {
            if (!isSignedNoteLockError(err)) throw err;
          }

          if (isSharedGroupNote && currentResponse.id) {
            // Persist signature fields first so the bot can read the latest QR content.
            updated = await medplum.updateResource(updated);
            try {
              await requestGroupSharedNoteFanout(
                medplum,
                currentResponse.id,
                appointment.id,
                { strict: true },
              );
            } catch (fanoutErr) {
              console.error('Group note fan-out failed:', fanoutErr);
              notify({
                type: 'error',
                title: 'Cannot finalize shared note',
                error: fanoutErr,
                message: 'Patient-specific notes could not be generated. Resolve attendance and try again.',
              });
              throw fanoutErr;
            }
          }
        }

        // Mark note signed in detailed-status (draft, ai-draft, or legacy notes without status).
        updated = {
          ...updated,
          extension: setDetailedStatus(updated.extension ?? [], 'signed'),
        };
        let signedResponse: QuestionnaireResponse = { ...updated, status: 'completed' };
        if (!isMock) {
          try {
            signedResponse = await commitSignedNoteAndSealAppointment(
              medplum,
              signedResponse,
              appointment?.id,
              noteAttendance,
              {
                // Revise Note: the editor already saved the revised answers on a signed note.
                revision: appointmentNoteStatusLocksFulfillment(getDetailedStatus(currentResponse)),
              },
            );
          } catch (commitErr) {
            // The signature committed but the Appointment seal did not: show the note as signed
            // and offer a seal-only retry instead of reporting a failed sign.
            if (!isSealPendingError(commitErr)) throw commitErr;
            signedResponse = commitErr.signedNote;
            notifySealPending(medplum, commitErr, () => {
              void medplum
                .readResource('Appointment', commitErr.appointmentId, { cache: 'no-cache' })
                .then(setAppointment)
                .catch((readErr: unknown) => console.warn('Appointment re-read after seal failed:', readErr));
            });
          }

          void requestSignedNoteExport(medplum, signedResponse.id!).catch((exportErr) => {
            console.warn('Signed note PDF export failed:', exportErr);
            notify({
              type: 'warning',
              title: 'Signed PDF pending',
              message:
                getUserFacingErrorMessage(exportErr, 'The note was signed but the PDF export could not complete.'),
            });
          });
        }
        replaceResponse(currentResponse.id, signedResponse);
        if (localNoteEntry?.response.id === currentResponse.id) {
          setLocalNoteEntry({ ...localNoteEntry, response: signedResponse });
        }
        await createPendingAddendumAfterSign();
        setNoteMode('view');
        setIsAddendumEdit(false);
        return true;
      } catch (err) {
        console.error('Failed to sign note:', err);
        notify({
          type: 'error',
          title: 'Sign failed',
          error: err,
          message: 'Could not sign the note.',
        });
        return false;
      }
    },
    [
      currentResponse,
      isIndividualCohortNote,
      localNoteEntry,
      medplum,
      profile,
      replaceResponse,
      appointment,
      noteEncounter,
      noteChargeItems,
      noteEpisodeOfCare,
      createPendingAddendumAfterSign,
      isCareManager,
      isNoteAuthor,
    ],
  );

  const handleResolveComment = useCallback(
    async (commentId: string) => {
      if (!currentResponse?.id) return;
      const comments = getReviewCommentsFromQR(currentResponse);
      const nextComments = comments.map((c) =>
        c.id === commentId ? { ...c, resolvedAt: new Date().toISOString() } : c
      );
      const updated = setReviewCommentsOnQR(currentResponse, nextComments);
      const isMock = isMockQuestionnaireResponseId(currentResponse.id);
      if (!isMock) {
        await medplum.updateResource(updated);
      }
      replaceResponse(currentResponse.id, updated);
    },
    [currentResponse, medplum, replaceResponse],
  );

  const [exemplarToggleLoading, setExemplarToggleLoading] = useState(false);
  const isCurrentResponseExemplar = currentResponse ? isExemplarNote(currentResponse) : false;

  const handleToggleExemplar = useCallback(
    async (mark: boolean) => {
      if (!canManageExemplarNotes || !currentResponse?.id) return;
      if (isMockQuestionnaireResponseId(currentResponse.id)) {
        notify({
          type: 'warning',
          title: 'Exemplar tag unavailable',
          message: 'Exemplar notes can only be marked on saved clinical notes.',
        });
        return;
      }

      setExemplarToggleLoading(true);
      try {
        const updated = { ...currentResponse };
        setExemplarNoteTag(updated, mark);
        const saved = await medplum.updateResource(updated);
        replaceResponse(saved.id!, saved);
        setLocalNoteEntry((prev) =>
          prev?.response.id === saved.id ? { ...prev, response: saved } : prev,
        );
        medplum.invalidateSearches('QuestionnaireResponse');
        notify({
          type: 'success',
          title: mark ? 'Marked as exemplar' : 'Exemplar tag removed',
          message: mark
            ? 'This note will appear when selecting exemplar notes for its template in service type settings.'
            : 'This note will no longer appear as an exemplar option for its template.',
        });
      } catch (err) {
        notify({
          type: 'error',
          title: 'Could not update exemplar status',
          error: err,
          message: 'Please try again.',
        });
      } finally {
        setExemplarToggleLoading(false);
      }
    },
    [canManageExemplarNotes, currentResponse, medplum, replaceResponse],
  );

  // Previous response for "Load last note" (from same appointment; one note per appointment so usually null)
  const previousResponse: QuestionnaireResponse | null = null;

  const [outcomeTemplateAiDisabled, setOutcomeTemplateAiDisabled] = useState(false);

  /** undefined = use mock baseline; number | null = user override from modal (null = cleared). */
  const [sessionDurationLocal, setSessionDurationLocal] = useState<number | null | undefined>(undefined);

  // --- Confirm-read modal when saving soon after AI draft (< 2 min) ---
  const [confirmReadModalOpened, { open: openConfirmReadModal, close: closeConfirmReadModal }] =
    useDisclosure(false);
  const [signModalOpened, { open: openSignModal, close: closeSignModal }] = useDisclosure(false);
  const pendingSaveResolveRef = useRef<((value: boolean) => void) | null>(null);

  /**
   * Called by ProgressNoteForm before signing. If note was AI-generated recently (< 2 min),
   * shows confirm-read modal and returns a promise that resolves true when user confirms
   * (by typing name) or false when cancelled. Otherwise resolves true immediately.
   */
  const handleBeforeSign = useCallback((): Promise<boolean> => {
    if (getDetailedStatus(currentResponse ?? {}) !== 'ai-draft') {
      return Promise.resolve(true);
    }
    const authoredMs = currentResponse?.authored ? Date.parse(currentResponse.authored) : NaN;
    const elapsed = Number.isFinite(authoredMs) ? Date.now() - authoredMs : Infinity;
    if (elapsed >= AI_NOTETAKER_REVIEW_WINDOW_MS) {
      return Promise.resolve(true);
    }
    return new Promise<boolean>((resolve) => {
      pendingSaveResolveRef.current = resolve;
      openConfirmReadModal();
    });
  }, [currentResponse, openConfirmReadModal]);

  const noteDetailedStatus = currentResponse ? getDetailedStatus(currentResponse) : undefined;

  const aiNoteEditorLocked =
    generationLoading ||
    revisionLoading ||
    noteDetailedStatus === 'generation-in-progress' ||
    (awaitingAiDraft &&
      noteDetailedStatus !== 'ai-draft' &&
      noteDetailedStatus !== 'generation-failed');

  const aiNoteInitialGenerating =
    generationLoading ||
    noteDetailedStatus === 'generation-in-progress' ||
    (awaitingAiDraft &&
      noteDetailedStatus !== 'ai-draft' &&
      noteDetailedStatus !== 'generation-failed');

  const passExistingResponseToForm = Boolean(
    currentResponse &&
      (noteMode === 'edit' ||
        noteDetailedStatus === 'ai-draft' ||
        noteDetailedStatus === 'generation-failed' ||
        noteDetailedStatus === 'generation-in-progress' ||
        aiNoteEditorLocked),
  );

  const canGenerateAiNote = useMemo(
    () =>
      Boolean(
        appointmentId &&
        hasTranscript &&
        !aiNotetakerLocked &&
        aiNoteStatus !== 'generating',
      ),
    [appointmentId, hasTranscript, aiNotetakerLocked, aiNoteStatus],
  );

  const canRegenerateAiNote = useMemo(
    () =>
      Boolean(
        currentResponse?.id &&
        hasTranscript &&
        !aiNotetakerLocked &&
        !aiNoteInitialGenerating &&
        (noteDetailedStatus === 'ai-draft' ||
          noteDetailedStatus === 'draft' ||
          noteDetailedStatus === undefined),
      ),
    [currentResponse?.id, hasTranscript, aiNotetakerLocked, noteDetailedStatus, aiNoteInitialGenerating],
  );

  const handleRequestGeneration = useCallback(async (additionalPrompt: string) => {
    if (aiNotetakerLocked) {
      throw new UserFacingError('AI note generation is disabled while the note is signed.');
    }
    if (!appointmentId) {
      throw new Error('No appointment loaded.');
    }
    if (!hasTranscript) {
      throw new UserFacingError('Session transcript is not available yet.');
    }

    // Create a single trace shared by all frontend and backend events so they
    // land on the same trace ID (previously split into two separate traces).
    const aiRunId = `run-${Date.now().toString(36)}`;
    const recorder = createTimingRecorder({
      traceId: createAiGenerationTraceId(appointmentId, undefined, aiRunId),
      phase: 'ai_generation',
      appointmentId,
      aiRunId,
    });
    aiTimingRef.current = recorder;
    const timingContext: AiNoteGenerationTimingContext | undefined = recorder
      ? { traceId: recorder.traceId, aiRunId, scenario: 'ai_note_generation_baseline' }
      : undefined;

    recorder?.started('ai_generate_clicked_at', { scenario: 'ai_note_generation_baseline' });

    setGenerationLoading(true);
    setAwaitingAiDraft(true);
    textUndoApiRef.current?.captureTextUndoBaselines();
    try {
      const prompt = additionalPrompt.trim() || undefined;
      recorder?.started('ai_generate_request_sent_at');
      const sendStart = recorder?.now() ?? 0;
      try {
        await requestAiNoteGeneration(medplum, appointmentId, prompt, timingContext);
        recorder?.finished('ai_generate_response_returned_at', sendStart);
      } catch (err) {
        recorder?.failed('ai_generate_request_failed', err);
        throw err;
      }
      await refetchResponses();
      setLocalNoteEntry(null);
      textUndoApiRef.current?.remountForm();
    } catch (err) {
      recorder?.failed('ai_generate_failed', err);
      aiTimingRef.current = null;
      setAwaitingAiDraft(false);
      notify({
        type: 'error',
        title: 'Generation failed',
        error: err,
        message: 'Could not start AI note generation.',
      });
      throw err;
    } finally {
      setGenerationLoading(false);
    }
  }, [aiNotetakerLocked, appointmentId, hasTranscript, medplum, refetchResponses]);

  const handleRequestRevision = useCallback(async (additionalPrompt: string) => {
    if (aiNotetakerLocked) {
      throw new UserFacingError('AI note generation is disabled while the note is signed.');
    }
    const prompt = additionalPrompt.trim();
    if (!prompt) return;
    if (!currentResponse?.id) {
      throw new Error('No note to revise');
    }
    setRevisionLoading(true);
    textUndoApiRef.current?.captureTextUndoBaselines();
    try {
      const updated = await requestAiNoteRevision(medplum, currentResponse.id, prompt);
      replaceResponse(currentResponse.id, updated);
      setLocalNoteEntry((prev) =>
        prev?.response.id === currentResponse.id
          ? { response: updated, questionnaire: prev!.questionnaire }
          : prev,
      );
      textUndoApiRef.current?.remountForm();
      await refetchResponses();
      notify({
        type: 'success',
        title: 'Note regenerated',
        message: 'The AI draft has been updated. Review all fields before signing.',
      });
    } catch (err) {
      notify({
        type: 'error',
        title: 'Regeneration failed',
        error: err,
        message: 'Could not regenerate the note.',
      });
      throw err;
    } finally {
      setRevisionLoading(false);
    }
  }, [aiNotetakerLocked, currentResponse?.id, medplum, replaceResponse, refetchResponses]);

  const progressNoteContext = useMemo((): ProgressNoteContext | null => {
    if (!appointmentId || appointmentOnlyEarly) return null;
    return {
      kind: 'progress-note-generate',
      appointmentId,
      patientId: patient?.id,
      documentKey: `appointment:${appointmentId}`,
      geminiAiDisabled: geminiAiDisabled || aiNotetakerLocked,
      canGenerate: canGenerateAiNote && Boolean(encounterRef) && !outcomeTemplateAiDisabled,
      canRegenerate: canRegenerateAiNote && !outcomeTemplateAiDisabled,
      onGenerate: handleRequestGeneration,
      onRegenerate: handleRequestRevision,
    };
  }, [
    appointmentId,
    appointmentOnlyEarly,
    patient?.id,
    geminiAiDisabled,
    aiNotetakerLocked,
    canGenerateAiNote,
    canRegenerateAiNote,
    encounterRef,
    outcomeTemplateAiDisabled,
    handleRequestGeneration,
    handleRequestRevision,
  ]);
  useRegisterProgressNoteContext(progressNoteContext);
  const aiAssistant = useOptionalAiAssistant();
  // In-person recording runs on Chime in this page.
  const inPersonRecording = useChimeInPersonRecording(appointment?.id, setAppointment);
  const {
    hostStatus: inPersonHostStatus,
    setupPhase: inPersonSetupPhase,
    joinError: inPersonJoinError,
    elapsedSeconds: inPersonElapsedSeconds,
    isMuted: inPersonMuted,
    ending: inPersonEnding,
    starting: inPersonStarting,
    showRecordingControls: showInPersonRecordingControls,
    audioDevices: inPersonAudioDevices,
    selectedAudioDeviceId: inPersonSelectedAudioDeviceId,
    beginRecording: beginInPersonRecording,
    toggleMute: toggleInPersonMute,
    setAudioInputDevice: setInPersonAudioInputDevice,
    endSession: endInPersonSession,
    clearSetupError: clearInPersonSetupError,
    notice: inPersonNotice,
    elsewhereNotice: inPersonElsewhereNotice,
  } = inPersonRecording;

  // Session tools and Freud share the right edge — keep them mutually exclusive.
  useEffect(() => {
    if (aiAssistant?.open) setSessionToolsOpen(false);
  }, [aiAssistant?.open]);

  const handleOpenSessionTools = useCallback(() => {
    aiAssistant?.closeDrawer();
    setSessionToolsOpen(true);
  }, [aiAssistant]);

  const handleBeginInPersonRecording = useCallback(async () => {
    aiAssistant?.closeDrawer();
    setSessionToolsOpen(true);
    await beginInPersonRecording();
  }, [aiAssistant, beginInPersonRecording]);

  // Keep session tools open while recording is active/connecting.
  useEffect(() => {
    if (showInPersonRecordingControls || inPersonStarting) {
      aiAssistant?.closeDrawer();
      setSessionToolsOpen(true);
    }
  }, [aiAssistant, inPersonStarting, showInPersonRecordingControls]);

  const handleEndInPersonRecording = useCallback(() => {
    void endInPersonSession()
      .then((ended) => {
        // A declined End (Chime: the meeting was replaced on another device) is explained by the hook's notice.
        if (ended) notify({ type: 'success', message: 'Recording session ended' });
      })
      .catch((e) => {
        notify({
          type: 'error',
          error: e,
          message: 'Could not end recording',
        });
      });
  }, [endInPersonSession]);

  const handleConfirmReadConfirm = useCallback(() => {
    const resolved = pendingSaveResolveRef.current;
    pendingSaveResolveRef.current = null;
    closeConfirmReadModal();
    resolved?.(true);
  }, [closeConfirmReadModal]);

  const handleConfirmReadCancel = useCallback(() => {
    const resolved = pendingSaveResolveRef.current;
    pendingSaveResolveRef.current = null;
    closeConfirmReadModal();
    resolved?.(false);
  }, [closeConfirmReadModal]);

  const allPatientParticipants = useMemo(() => {
    if (!appointment) return [];
    return enrichAppointmentAttendeeDisplayNames(
      getAllPatientParticipantsFromAppointment(appointment),
      editorTables.patientsById,
      editorTables.relatedPersonsById,
    );
  }, [appointment, editorTables.patientsById, editorTables.relatedPersonsById]);
  const isGroupSession = allPatientParticipants.length > 1;
  const isCohortAppointment = Boolean(appointment && isCohortGroupAppointment(appointment));
  const isGroupAppointmentEdit = isGroupSession || isCohortAppointment;
  const cohortGroup = useMemo(() => {
    const ref = appointment ? getAppointmentGroupRef(appointment) : undefined;
    const id = ref?.reference?.replace(/^Group\//, '');
    return id ? { id, name: ref?.display?.trim() || 'Group session' } : undefined;
  }, [appointment]);

  // -------------------------------------------------------------------------
  // Render: loading state
  // -------------------------------------------------------------------------

  if (loading) {
    return <Loading />;
  }

  if ((viewScope === 'OWN' && practitionerLoading) || superviseesLoading) {
    return <Loading />;
  }

  // -------------------------------------------------------------------------
  // Render: error / not found
  // -------------------------------------------------------------------------

  if (error || !appointment) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={20} />} title="Appointment Not Found" color="red">
            {error || 'The requested appointment could not be found.'}
          </Alert>
          <Text
            component={Link}
            to="/dashboard/calendar"
            c="blue.6"
            size="sm"
            style={{ textDecoration: 'underline' }}
          >
            Back to Calendar
          </Text>
        </Stack>
      </Container>
    );
  }

  const clientChartHref = patient?.id ? `/dashboard/clients/${patient.id}` : undefined;

  const episodeAccessDenied = Boolean(
    episodeOfCareForAccess &&
      !canViewMedplumEpisodeOfCare(episodeOfCareForAccess, appointmentAccessCtx),
  );
  const appointmentOnly =
    mockState?.appointmentOnly === true || mockState?.documentationOnly === true;

  if (episodeAccessDenied && !appointmentOnly) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="md">
          <Alert icon={<IconShield size={20} />} title="Access denied" color="red" variant="light">
            This appointment is tied to an episode of care you are not a participant on. You can still see it listed on
            the client medical record and open the appointment for session details; clinical documentation is not
            available.
          </Alert>
          <Text
            component={Link}
            to={clientChartHref ?? '/dashboard/calendar'}
            c="blue.6"
            size="sm"
            style={{ textDecoration: 'underline' }}
          >
            {clientChartHref ? 'Back to client chart' : 'Back to Calendar'}
          </Text>
        </Stack>
      </Container>
    );
  }

  // -------------------------------------------------------------------------
  // Render: main layout
  // -------------------------------------------------------------------------

  const providerDisplayName = practitionerDisplayLabelFromAppointment(
    appointment,
    appointmentPractitioner ?? undefined,
  );
  const viewerIsAppointmentParticipant = Boolean(
    practitioner?.id
    && actorRefIsAppointmentParticipant(
      appointment.participant,
      `Practitioner/${practitioner.id}`,
    ),
  );

  const sessionType =
    editorTables.sessionTypeName
    ?? getAppointmentServiceTypeLabel(appointment);

  const noteEditorActive =
    !appointmentOnly
    && mayAuthorClinicalNote
    && !(isIndividualCohortNote && hasNoNote)
    && (noteMode === 'add' || noteMode === 'edit' || hasNoNote);

  /** Shared by header Edit and Session Summary pencil — opens SessionSchedulerModal. */
  const handleEditAppointment = appointmentOnly
    ? undefined
    : () => setEditAppointmentOpen(true);

  const patientDisplayName = patient?.name?.[0]
    ? `${patient.name[0].given?.join(' ') ?? ''} ${patient.name[0].family ?? ''}`.trim() || 'Client'
    : 'Client';

  const appointmentBreadcrumbTitle = buildAppointmentDisplayTitle({
    groupDisplayName:
      isCohortAppointment && !isIndividualCohortNote ? cohortGroup?.name : undefined,
    episodeDisplayName,
    clientDisplayName:
      isCohortAppointment && !isIndividualCohortNote ? undefined : patientDisplayName,
  });

  const breadcrumbFrom =
    chartPatientId || mockState?.breadcrumbFrom === 'client-chart' ? 'client-chart' : 'calendar';
  const breadcrumbParts =
    breadcrumbFrom === 'client-chart' && patient?.id
      ? [
        { title: patientDisplayName, href: `/dashboard/clients/${patient.id}` },
        { title: 'Chart', href: `/dashboard/clients/${patient.id}?tab=chart` },
        { title: appointmentBreadcrumbTitle, href: '' },
      ]
      : [
        { title: 'Calendar', href: '/dashboard/calendar' },
        ...(patient?.id ? [{ title: patientDisplayName, href: `/dashboard/clients/${patient.id}` }] : []),
        { title: appointmentBreadcrumbTitle, href: '' },
      ];
  const effectiveSessionDuration =
    sessionDurationLocal !== undefined ? sessionDurationLocal : mockState?.sessionDurationMinutes ?? null;

  const noteHeaderActions = (
    <NoteAiToolbar
      canGenerate={
        (canRegenerateAiNote || (canGenerateAiNote && Boolean(encounterRef)))
        && !geminiAiDisabled
        && !outcomeTemplateAiDisabled
      }
      hasDraft={canRegenerateAiNote}
      generateLoading={generationLoading || aiNoteStatus === 'generating'}
      geminiAiDisabled={geminiAiDisabled}
      outcomeTemplateAiDisabled={outcomeTemplateAiDisabled}
      disabled={aiNotetakerLocked}
      onGenerateClick={() => aiAssistant?.startProgressNoteGenerateFlow()}
    />
  );

  const breadcrumbItems = breadcrumbParts.map((item, index) => {
    const isLast = index === breadcrumbParts.length - 1;
    if (isLast && !item.href) {
      const clientForTitle =
        isCohortAppointment && !isIndividualCohortNote ? undefined : patientDisplayName;
      return (
        <AppointmentTitleText
          key={index}
          title={item.title}
          clientDisplayName={clientForTitle}
          clientChartHref={
            clientForTitle && patient?.id ? `/dashboard/clients/${patient.id}` : undefined
          }
          size="sm"
          c="gray.6"
        />
      );
    }
    return item.href ? (
      <Text key={index} component={Link} to={item.href} c="gold.7" size="sm" fw={500}>
        {item.title}
      </Text>
    ) : (
      <Text key={index} size="sm" c="gray.6">
        {item.title}
      </Text>
    );
  });

  return (
    <SessionToolsDrawer
      open={sessionToolsOpen}
      onOpenChange={setSessionToolsOpen}
      showOpenButton={false}
      main={
    <SessionMetadataUiProvider>
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Back button + breadcrumbs */}
        <Group gap="xs" align="center">
          <ActionIcon
            variant="subtle"
            color="gold.7"
            size="lg"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <IconArrowLeft size={18} />
          </ActionIcon>
          <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
        </Group>

        <PatientHeader
          /** Shared cohort view identifies the Group; chart patient context shows that client. */
          patient={isCohortAppointment && !isIndividualCohortNote ? null : patient}
          loading={loading}
          groupDisplayName={
            isCohortAppointment && !isIndividualCohortNote ? cohortGroup?.name : undefined
          }
          episodeDisplayName={episodeDisplayName}
          providerDisplayName={providerDisplayName}
          appointmentStart={appointment.start}
          appointmentEnd={appointment.end}
          sessionType={sessionType}
          attendanceStatus={appointment.status}
          attendanceStatusLabel={getAppointmentWorkflowStatusLabel(appointment)}
          attendanceStatusBadgeColor={getAppointmentWorkflowStatusBadgeColor(appointment)}
          sessionDurationMinutes={effectiveSessionDuration}
          previousAppointment={previousAppt}
          nextAppointment={nextAppt}
          videoMeetingLink={getVideoMeetingUrl(appointment)}
          appointmentId={appointment.id}
          sessionModality={resolveAppointmentModality(appointment)}
          sessionRecordingConsented={sessionRecordingConsented}
          onBeginInPersonRecording={handleBeginInPersonRecording}
          onEndInPersonRecording={handleEndInPersonRecording}
          recordingStarting={inPersonStarting}
          recordingEnding={inPersonEnding}
          recordingSetupPhase={inPersonSetupPhase}
          recordingSetupError={inPersonJoinError}
          recordingConsentOnFile={sessionRecordingConsented}
          recordingHostJoined={inPersonHostStatus === 'joined'}
          onClearRecordingSetupError={clearInPersonSetupError}
          serviceCodeRows={editorTables.serviceCodeRows}
          participantRows={editorTables.participantRows}
          onEditAppointment={handleEditAppointment}
          onOpenSessionTools={handleOpenSessionTools}
          onAttendanceChange={
            appointmentFieldsLocked ? undefined : editorTables.onAttendanceChange
          }
          savedStatusByPatientId={editorTables.savedAttendanceByPatientId}
          onLocationChange={
            appointmentFieldsLocked ? undefined : editorTables.onLocationChange
          }
          locationOptionsByPatientId={editorTables.locationOptionsByPatientId}
          showHeaderActions={!appointmentFieldsLocked}
          viewerIsParticipant={viewerIsAppointmentParticipant}
        />

        {appointment.status === 'proposed' &&
        appointment.extension?.some(
          (e) => e.url === 'https://unify.health/fhir/StructureDefinition/portal-reschedule-original-appointment',
        ) &&
        appointment.id ? (
          <Alert color="violet" variant="light" title="Client reschedule request">
            <Stack gap="xs">
              <Text size="sm">
                A client requested to move an appointment to this time. Accept to confirm or decline to remove the
                proposed time from the calendar.
              </Text>
              <RescheduleRequestActions
                proposedAppointmentId={appointment.id}
                originalAppointmentId={
                  appointment.extension
                    ?.find(
                      (e) =>
                        e.url
                        === 'https://unify.health/fhir/StructureDefinition/portal-reschedule-original-appointment',
                    )
                    ?.valueReference?.reference?.slice('Appointment/'.length)
                }
                onCompleted={() => void refreshAppointmentFromServer()}
              />
            </Stack>
          </Alert>
        ) : null}

        {inPersonElsewhereNotice ? (
          // Outside the recording controls: they are hidden once this device has lost the room. Cleared on Start.
          <Alert icon={<IconAlertCircle size={18} />} color="orange" variant="light" title="Recording stopped on this device">
            {inPersonElsewhereNotice}
          </Alert>
        ) : null}

        {appointmentOnly && (
          <Alert icon={<IconShield size={18} />} color="gray" variant="light" title="Clinical documentation unavailable">
            This session belongs to another episode of care. You can view appointment details here; clinical notes and
            session tools are only available to that episode’s care team.
          </Alert>
        )}

        {/* Full-width note — AI lives in Freud drawer */}
        {!appointmentOnly && (
        <Stack gap="md">
            {isIndividualCohortNote && hasNoNote && (
              <Card withBorder padding="lg">
                <Stack gap="sm">
                  <CohortProgressNoteTitleExtras
                    noteKind="individual"
                    sharedNoteHref={sharedSessionNoteHref}
                  />
                  <Text size="sm" c="dimmed">
                    No individual Group Therapy note for this client yet. Open the shared session
                    note to document the group; an individual copy is created when attendance is
                    resolved and the note is saved or signed.
                  </Text>
                  {sharedSessionNoteHref ? (
                    <Button
                      component={Link}
                      to={sharedSessionNoteHref}
                      variant="light"
                      color="gold"
                      w="fit-content"
                    >
                      Open shared session note
                    </Button>
                  ) : null}
                </Stack>
              </Card>
            )}

            {noteMode === 'view' && !hasNoNote && (
              <Stack gap="md">
              <ProgressNoteCard
                questionnaireResponse={currentResponse}
                questionnaire={currentQuestionnaire}
                loading={responsesInitiallyLoading}
                showSupervisorSignature={hasSupervisor}
                addendumCount={addendums.length}
                addendums={addendums.length > 0 ? addendums : undefined}
                originalNoteDate={addendums.length > 0 ? originalNoteDate : undefined}
                originalAuthorName={providerSignature?.name ?? providerDisplayName}
                headerBadge={noteHeaderBadge}
                onEdit={!canEditDraft ? undefined : handleEditDraft}
                onEditAddendum={addendumFlowEligible ? handleAddendumEdit : undefined}
                onSignClick={isIndividualCohortNote || !canSignNote ? undefined : openSignModal}
                onResolveComment={handleResolveComment}
                signReviewWarning={signReviewWarning}
                automaticChecks={serverChecks}
                sessionTiming={sessionTiming}
                showExemplarControls={canManageExemplarNotes}
                isExemplarNote={isCurrentResponseExemplar}
                exemplarToggleLoading={exemplarToggleLoading}
                onToggleExemplar={
                  canManageExemplarNotes
                    ? (mark) => void handleToggleExemplar(mark)
                    : undefined
                }
                cohortNoteKind={cohortNoteKind}
                sharedNoteHref={sharedSessionNoteHref}
              />
              {mayViewPsychotherapyNote && appointmentId && patientRef ? (
                <Card withBorder padding="lg">
                  <PsychotherapyNoteSection
                    appointmentId={appointmentId}
                    patientRef={{ reference: patientRef }}
                    encounterRef={encounterRef ? { reference: encounterRef } : undefined}
                    allowWrite={mayAuthorClinicalNote}
                  />
                </Card>
              ) : null}
              </Stack>
            )}

            {noteEditorActive && appointmentId && (
              <Stack gap="md">
              <ProgressNoteForm
                appointmentId={appointmentId}
                appointment={appointment}
                patientRef={patientRef}
                encounterRef={encounterRef}
                encounter={noteEncounter}
                chargeItems={noteChargeItems}
                episodeOfCare={noteEpisodeOfCare ?? episodeOfCareForAccess}
                existingResponse={passExistingResponseToForm ? currentResponse : null}
                existingQuestionnaire={passExistingResponseToForm ? currentQuestionnaire : null}
                previousResponse={previousResponse}
                onSave={handleSaveNote}
                onCancel={handleCancelEdit}
                onAppointmentUpdated={setAppointment}
                onBeforeSign={handleBeforeSign}
                appointmentResponsesLoading={responsesLoading}
                onRequestSign={
                  !isIndividualCohortNote && canSignNote && (canEditDraft || isAddendumEdit)
                    ? openSignModal
                    : undefined
                }
                editIntent={isAddendumEdit ? 'addendum' : 'draft'}
                onFinishEdit={isAddendumEdit ? handleFinishEdit : undefined}
                onBeforeResign={isAddendumEdit ? handleBeforeResign : undefined}
                textUndoApiRef={textUndoApiRef}
                autosavePaused={aiNoteEditorLocked}
                editorLocked={aiNoteEditorLocked}
                onOutcomeTemplateAiDisabledChange={setOutcomeTemplateAiDisabled}
                headerBadge={noteHeaderBadge}
                headerActions={noteHeaderActions}
                onEditAppointment={handleEditAppointment}
                externalParticipantLocationByPatientId={editorTables.locationByPatientId}
                cohortNoteKind={cohortNoteKind}
                sharedNoteHref={sharedSessionNoteHref}
              />
              {mayViewPsychotherapyNote && patientRef ? (
                <Card withBorder padding="lg">
                  <PsychotherapyNoteSection
                    appointmentId={appointmentId}
                    patientRef={{ reference: patientRef }}
                    encounterRef={encounterRef ? { reference: encounterRef } : undefined}
                    allowWrite={mayAuthorClinicalNote}
                  />
                </Card>
              ) : null}
              </Stack>
            )}
        </Stack>
        )}
      </Stack>

      <ConfirmAiNoteReadModal
        opened={confirmReadModalOpened}
        profileDisplayName={profileDisplayName}
        onConfirm={handleConfirmReadConfirm}
        onCancel={handleConfirmReadCancel}
      />

      <SignatureCaptureModal
        opened={signModalOpened}
        onClose={closeSignModal}
        onSign={async (signature) => {
          const signed = await handleSignNote(signature);
          if (signed) closeSignModal();
        }}
        title="Sign progress note"
        submitLabel="Sign note"
        reviewWarning={signReviewWarning}
        jurisdictionState={patient ? getPatientHomeState(patient) : undefined}
      />

      {practitioner?.id
      && organizationId
      && (isGroupAppointmentEdit || (patient?.id && episodeOfCareIdForSources)) ? (
        <AppointmentEditorModal
          source="appointment-page"
          opened={editAppointmentOpen}
          onClose={() => setEditAppointmentOpen(false)}
          appointment={appointment}
          organizationId={organizationId}
          viewerPractitionerId={practitioner.id}
          groupSession={isGroupAppointmentEdit}
          episodeOfCareId={isGroupAppointmentEdit ? undefined : episodeOfCareIdForSources}
          patientId={isGroupAppointmentEdit ? undefined : patient?.id}
          patientDisplay={isGroupAppointmentEdit ? undefined : patientDisplayName}
          episodeDisplayName={episodeDisplayName}
          groupDisplayName={
            isCohortAppointment && !isIndividualCohortNote ? cohortGroup?.name : undefined
          }
          tables={editorTables}
          videoLink={getVideoMeetingUrl(appointment)}
          readOnly={appointmentFieldsLocked}
          showJoinVideo={
            shouldShowCalendarJoin({
              isTelehealthOrHybrid:
                resolveAppointmentModality(appointment) === 'telehealth'
                || resolveAppointmentModality(appointment) === 'hybrid',
              isParticipant: viewerIsAppointmentParticipant,
              hasVideoMeeting: Boolean(getVideoMeetingUrl(appointment)?.trim()),
            })
          }
          onSaved={(saved) => {
            setAppointment(saved);
            setEditAppointmentOpen(false);
            void refreshAppointmentFromServer();
            refetchNoteSources();
          }}
        />
      ) : null}

      {/* Warning modal shown before entering edit mode on a signed/locked note */}
      <Modal
        opened={lockedNoteEditWarningOpen}
        onClose={closeLockedNoteEditWarning}
        title="Edit locked medical record"
        size="md"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="gray.7" lh={1.65}>
            You are about to edit a signed and locked note. A revised note will be created automatically
            to document your changes.
          </Text>
          <Text size="sm" c="gray.7" lh={1.65}>
            Every change will be tracked and visible in the change history. The original signed note
            will be preserved.
          </Text>
          <Text size="sm" fw={500} c="gray.8">
            Are you sure you want to edit this note?
          </Text>
          <Group justify="flex-end" gap="sm" mt="xs">
            <Button variant="default" onClick={closeLockedNoteEditWarning}>
              Cancel
            </Button>
            <Button
              color="gold"
              leftSection={<IconEdit size={14} />}
              onClick={handleConfirmLockedNoteEdit}
            >
              Yes, edit note
            </Button>
          </Group>
        </Stack>
      </Modal>

      <AddendumReasonModal
        opened={addendumReasonModalOpen}
        onClose={handleAddendumReasonClose}
        onSubmit={handleAddendumReasonSubmit}
      />

    </Container>
    </SessionMetadataUiProvider>
      }
    >
      <Box h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
        <SessionClinicalPanelContent
          appointmentId={appointment?.id}
          layout="overlay"
          onClose={() => setSessionToolsOpen(false)}
          includeRecordingTab={showInPersonRecordingControls}
          recordingTabContent={
            showInPersonRecordingControls ? (
              <Stack gap="sm">
                {inPersonHostStatus === 'starting' ? (
                  <Text size="sm" c="dimmed" px="md" pt="md">
                    Connecting recording host…
                  </Text>
                ) : null}
                {inPersonJoinError ? (
                  <Text size="sm" c="red" px="md" pt="md">
                    {inPersonJoinError}
                  </Text>
                ) : null}
                {inPersonNotice ? (
                  <Text size="sm" c="orange" px="md" pt="md">
                    {inPersonNotice}
                  </Text>
                ) : null}
                <RecordingControlsTab
                  elapsedSeconds={inPersonElapsedSeconds}
                  isMuted={inPersonMuted}
                  ending={inPersonEnding}
                  audioDevices={inPersonAudioDevices}
                  selectedAudioDeviceId={inPersonSelectedAudioDeviceId}
                  onToggleMute={toggleInPersonMute}
                  onAudioDeviceChange={setInPersonAudioInputDevice}
                  onEndMeeting={handleEndInPersonRecording}
                />
              </Stack>
            ) : null
          }
        />
      </Box>
    </SessionToolsDrawer>
  );
}
